<template>
    <div>
        <div class="grid">
            <div v-for="template in templates" :key="template.key"
                :class="{ inactive: !value[template.key]?.active, disabled: value[template.key]?.disabled }">

                <b-popover v-if="!value[template.key]?.disabled && value[template.key]?.active"
                    :target="getRefId(template.key)" triggers="focus" placement="top"
                    custom-class="text-message-template">

                    <div v-if="template.preview">
                        <h6>Preview</h6>
                        <div class="msg-bubble">{{ template.preview }}</div>
                    </div>

                    <div v-if="template.tokens && template.tokens.length">
                        <h6>Available Tokens <small class="text-muted">Click to add</small></h6>
                        <div class="tokens">
                            <code v-for="tkn in template.tokens" :key="tkn" @click="addToken(template.key, tkn)">
                                {{ "{" + tkn + "}" }}
                            </code>
                        </div>
                    </div>

                    <div v-if="defaults && defaults[template.key]">
                        <b-button href="#" variant="primary" size="sm" class="m-0" block
                            @click.prevent="updateValue(template.key, 'template', defaults[template.key].template)">
                            Load Default Template
                        </b-button>
                    </div>
                </b-popover>

                <card plain class="mb-0">
                    <div class="d-flex justify-content-between">
                        <label class="d-flex align-items-center">
                            <b-checkbox switch @input="updateValue(template.key, 'disabled', !$event)" size="sm"
                                :checked="!value[template.key]?.disabled" :disabled="disabled" />
                            <strong>{{ template.label }}</strong>
                            <HelpPopup v-if="template.helpText" placement="top" :text="template.helpText" />
                        </label>
                        <label class="d-flex align-items-center" v-if="!value[template.key]?.disabled">
                            <b-checkbox switch @input="updateValue(template.key, 'active', $event)" size="sm"
                                :checked="value[template.key]?.active" :disabled="disabled" />
                            <small class="text-muted">Use Custom Template</small>
                        </label>
                    </div>
                    <b-form-textarea v-if="!value[template.key]?.disabled && value[template.key]?.active"
                        :ref="getRefId(template.key)" :id="getRefId(template.key)"
                        :value="value[template.key]?.template" @input="updateValue(template.key, 'template', $event)"
                        spellcheck="false" :disabled="disabled" class="textarea-autosize" />
                </card>
            </div>
        </div>

    </div>
</template>

<script lang="ts">
import { Vue, Component, Prop } from 'vue-property-decorator';
import axios from "axios";
import { NotificationOptions } from '@/util/NotificationOptionsPresets';
import { DefaultTemplates, TemplateDefinition, TemplateValue } from "@/models/CustomTextMessageTemplate";
import HelpPopup from "@/components/HelpPopup.vue";

const pharmacyTokens: string[] = ["PharmacyName", "PharmacyPhone"];
const patientTokens: string[] = ["PatientFirstName", "PatientLastName"];
const linkExample: string = "https://link.com/ex";
const dateExample: string = "Monday, June 5";

@Component({
    name: 'TextMessageTemplatesField',
    components: { HelpPopup },
})
export default class TextMessageTemplatesField extends Vue {

    @Prop({ default: () => ({}) }) value!: Record<string, TemplateValue>;
    @Prop({ default: () => ({}) }) previewData!: Record<string, string>;
    @Prop({ default: () => [] }) hideTemplates!: string[];
    @Prop({
        type: Boolean,
        default: false,
    }) disabled!: boolean;

    // Data properties
    protected defaults: DefaultTemplates | null = null;
    private defaultPreviewReplacements: Record<string, string> = {
        PharmacyName: "Pharmacy Name",
        PharmacyPhone: "(800) 555-1212",
        PharmacyFax: "(888) 555-4444",
        PharmacyNPI: "123456",
        PharmacyNCPDP: "123456",
        PatientFirstName: "John",
        PatientLastName: "Doe",
        ConfirmLink: linkExample,
        FromPrescriberMessage: " from Dr. Livingston",
        NoCostMessage: "Your medication is available at no cost to you. ",
        OrderLink: linkExample,
        NoCopayMessage: "Your refill may be available at no cost to you. ",
        Password: "Pass123@!",
        AutorefillLink: linkExample,
        DaysToGoMessage: "in 3 days",
        TrackingNumber: "961013852890",
        DeliveryDate: dateExample,
        NextFillDate: dateExample,
        PaymentLink: linkExample,
        Link: linkExample,
        PharmacyFullAddress: "123 Test Lane, Chicago, IL, 60000",
        PAPLink: linkExample,
        DrugAbbreviation: "DRG",
    };

    // Lifecycle Hook
    // noinspection JSUnusedGlobalSymbols
    async mounted() {
        try {
            const response = await axios.get("/api/Configuration/text-message-template-field-defaults");

            this.defaults = response.data.defaults;
            const values = response.data.values;

            if (values.pharmacyName) this.defaultPreviewReplacements.PharmacyName = values.pharmacyName;
            if (values.pharmacyPhone) this.defaultPreviewReplacements.PharmacyPhone = values.pharmacyPhone;
            if (values.pharmacyFax) this.defaultPreviewReplacements.PharmacyFax = values.pharmacyFax;
            if (values.pharmacyNPI) this.defaultPreviewReplacements.PharmacyNPI = values.pharmacyNPI;
            if (values.pharmacyNCPDP) this.defaultPreviewReplacements.PharmacyNCPDP = values.pharmacyNCPDP;

        } catch (error) {
            console.error("Failed to load text message template defaults:", error);
            this.$notification(NotificationOptions.error(error));
        }
    }

    // Methods
    updateValue(templateName: string, fieldName: keyof TemplateValue, value: string | boolean): void {
        const newValue = { ...this.value };
        if (!newValue[templateName]) {
            newValue[templateName] = {};
        }
        // Type assertion needed because fieldName determines value type
        if (fieldName === 'active') {
            newValue[templateName][fieldName] = value as boolean;
        } else if (fieldName === 'disabled') {
            newValue[templateName][fieldName] = value as boolean;
        } else if (fieldName === 'template') {
            newValue[templateName][fieldName] = value as string;
        }
        this.$emit('input', newValue);
    }

    getPreviewMessage(templateString: string | undefined, tokens: string[]): string | undefined {
        if (!templateString) return undefined;

        let preview = templateString;
        tokens.forEach(t => {
            const replacement = (this.previewData && this.previewData[t] !== undefined)
                ? this.previewData[t]
                : this.defaultPreviewReplacements[t];
            if (replacement !== undefined) {
                preview = preview.replace(new RegExp(`{${t}}`, 'gi'), replacement);
            }
        });
        return preview;
    }

    addToken(key: string, token: string): void {
        const currentValue = this.value[key]?.template ?? "";
        const refId = this.getRefId(key);
        const refComponent = (this.$refs[refId] as Array<any>)[0];
        const textArea = refComponent?.$el as HTMLTextAreaElement;

        if (!textArea) {
            console.warn(`Textarea ref '${refId}' not found.`, refComponent);
            return;
        }

        const cursorStartPosition = textArea.selectionStart ?? currentValue.length;
        const cursorEndPosition = textArea.selectionEnd ?? currentValue.length;
        const textToInsert = `{${token}}`;

        const newValue = `${currentValue.slice(0, cursorStartPosition)}${textToInsert}${currentValue.slice(cursorEndPosition)}`;
        this.updateValue(key, 'template', newValue);

        this.$nextTick(() => {
            const newPosition = cursorStartPosition + textToInsert.length;
            textArea.selectionStart = textArea.selectionEnd = newPosition;
            textArea.focus();
        });
    }

    getRefId(key: string): string {
        return `text-message-template-${key}`;
    }

    get templates(): TemplateDefinition[] {
        const baseTemplates: Omit<TemplateDefinition, 'preview'>[] = [
            {
                label: "Welcome",
                key: "welcome",
                tokens: pharmacyTokens,
                helpText: "Sent automatically when a new e-script is received. Welcomes the patient to the pharmacy.",
            },
            {
                label: "NPC",
                key: "npc",
                tokens: [...pharmacyTokens, ...patientTokens, "ConfirmLink", "FromPrescriberMessage", "NoCostMessage", "DrugAbbreviation"],
                helpText: "Sent to patients with processed prescriptions awaiting confirmation. Includes a link to confirm or decline medications.",
            },
            {
                label: "NPC Order Created",
                key: "npcOrderCreated",
                tokens: [...pharmacyTokens, ...patientTokens, "DrugAbbreviation"],
                helpText: "Sent when a new order is created from prescriptions awaiting patient contact. Light notification confirming order creation.",
            },
            {
                label: "NPC Over Limit",
                key: "npcOverLimit",
                tokens: [...pharmacyTokens, ...patientTokens, "FromPrescriberMessage", "NoCostMessage", "DrugAbbreviation"],
                helpText: "Sent when patient copay exceeds the configured threshold. Asks patient to call the pharmacy instead of providing a payment link.",
            },
            {
                label: "Refill",
                key: "refill",
                tokens: [...pharmacyTokens, ...patientTokens, "OrderLink", "NoCopayMessage", "DrugAbbreviation"],
                helpText: "Sent to patients with prescriptions due for refill. Includes a link to the checkout/order page.",
            },
            {
                label: "Rx Request Denied",
                key: "rxRequestDenied",
                tokens: [...pharmacyTokens, "PatientFirstName"],
                helpText: "Sent manually when a prescriber denies a prescription request. Instructs patient to contact their doctor.",
            },
            {
                label: "Out of Network",
                key: "outOfNetwork",
                tokens: [...pharmacyTokens, ...patientTokens, "ConfirmLink"],
                helpText: "Sent when a prescription cannot be filled by the current pharmacy. Asks patient to confirm transfer to a network pharmacy.",
            },
            {
                label: "Password Reset",
                key: "passwordReset",
                tokens: [...pharmacyTokens, ...patientTokens, "Password"],
                helpText: "Sent when a patient requests a password reset in the patient app. Includes temporary password and instructions.",
            },
            {
                label: "Auto Refill",
                key: "autorefill",
                tokens: [...pharmacyTokens, ...patientTokens, "AutorefillLink", "DaysToGoMessage", "DrugAbbreviation"],
                helpText: "Sent to notify patients about upcoming auto-refill subscriptions. Includes days until next fill and management link.",
            },
            {
                label: "Auto Refill Delayed",
                key: "autorefillDelay",
                tokens: [...pharmacyTokens, ...patientTokens, "NextFillDate", "AutorefillLink", "DrugAbbreviation"],
                helpText: "Sent when an auto-refill date is postponed. Notifies patient of the new scheduled fill date.",
            },
            {
                label: "Transfer",
                key: "transfer",
                tokens: [...pharmacyTokens, ...patientTokens],
                helpText: "Sent when a prescription transfer fails. Notifies patient that the pharmacy is contacting the prescriber for a new prescription.",
            },
            {
                label: "Shipping",
                key: "shipping",
                tokens: [...pharmacyTokens, "TrackingNumber", "DeliveryDate", "DrugAbbreviation"],
                helpText: "Sent when packages ship. Includes tracking number and anticipated delivery date.",
            },
            {
                label: "Partial Payment",
                key: "partialPayment",
                tokens: [...pharmacyTokens, ...patientTokens, "PaymentLink", "DrugAbbreviation"],
                helpText: "Sent when patient has remaining balance below threshold after partial payment. Includes link to complete payment.",
            },
            {
                label: "Partial Payment Over Limit",
                key: "partialPaymentOverLimit",
                tokens: [...pharmacyTokens, ...patientTokens, "DrugAbbreviation"],
                helpText: "Sent when patient has remaining balance above threshold after partial payment. Asks patient to call the pharmacy.",
            },
            {
                label: "Awaiting Insurance",
                key: "awaitingInsurance",
                tokens: [...pharmacyTokens, ...patientTokens, "Link"],
                helpText: "Sent when prescription requires insurance information. Asks patient to provide insurance details via link.",
            },
            {
                label: "Pharmacy Info",
                key: "pharmacyInfo",
                tokens: [...pharmacyTokens, "PharmacyFax", "PharmacyNPI", "PharmacyNCPDP", "PharmacyFullAddress", ...patientTokens],
                helpText: "Sent manually to provide pharmacy contact information (phone, fax, NPI, NCPDP, address) to patient.",
            },
            {
                label: "Ready for Pickup",
                key: "readyForPickup",
                tokens: [...pharmacyTokens, "PharmacyFullAddress", ...patientTokens],
                helpText: "Sent when a Will-Call order is marked ready for pickup. Includes pharmacy address.",
            },
            {
                label: "Delivery Confirmation",
                key: "deliveryConfirmation",
                tokens: [...pharmacyTokens, ...patientTokens, "Link", "DeliveryDate", "DrugAbbreviation"],
                helpText: "Sent after package delivery. Asks patient to confirm receipt of shipment via link.",
            },
            {
                label: "Patient Assistance Program",
                key: "patientAssistanceProgram",
                tokens: [...pharmacyTokens, ...patientTokens, "PAPLink"],
                helpText: "Sent when inviting patients to apply for patient assistance programs.",
            },
        ];

        return baseTemplates
            .map(i => {
                const templateValue = this.value ? this.value[i.key] : undefined;
                return {
                    ...i,
                    preview: this.getPreviewMessage(templateValue?.template, i.tokens),
                } as TemplateDefinition;
            })
            .filter(i => !this.hideTemplates?.includes(i.key));
    }
}
</script>

<style scoped>
.grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(min(100%, 300px), 1fr));
    gap: 10px;
    margin-top: 10px;
}

@media screen and (min-width: 1200px) {
    .grid {
        grid-template-columns: repeat(3, 1fr);
    }
}

.grid>div>>>textarea {
    transition: all .2s;
    line-height: 1.45 !important;
}

.text-message-template .tokens code {
    display: inline-block;
    background-color: #f8f9fa;
    border: 1px solid #dee2e6;
    padding: 2px 5px;
    margin: 2px;
    border-radius: 3px;
    cursor: pointer;
    font-family: monospace;
}

.text-message-template .tokens code:hover {
    background-color: #e9ecef;
}

.text-message-template h6 {
    margin-top: 10px;
    margin-bottom: 5px;
    font-size: 0.9rem;
    font-weight: bold;
}

.text-message-template h6:first-child {
    margin-top: 0;
}
</style>
